import React from 'react';
import { useHistory } from 'react-router';

import { JobTeaserCardSkeleton } from '@/components/job-teaser';
import { JobCardSkeleton } from '@/domain/job-details/components/job-card/job-card-skeleton';
import { SimilarJobVisibleJobFragment } from '@/graphql/generated';
import { toQuery } from '@/helpers/query-string';
import { redirectUrl } from '@/helpers/redirect-url';
import { useJobWithNWTracking } from '@/hooks/use-job-with-NW-tracking';

type Props = {
  job?: SimilarJobVisibleJobFragment;
  trackingParam?: string;
  isLoading?: boolean;
  className?: string;
  trackingToken?: string;
  position?: number;
  template?: 'JobCard' | 'JobTeaserCard';
};

export const SimilarJob: React.VFC<Props> = ({
  job,
  trackingParam,
  position = 0,
  trackingToken = '',
  isLoading,
  className,
  template = 'JobTeaserCard',
}) => {
  const history = useHistory();

  const SimilarJobWithNWTTracking = useJobWithNWTracking({ template });

  if (isLoading) {
    if (template === 'JobCard') {
      return <JobCardSkeleton />;
    } else {
      return <JobTeaserCardSkeleton className={className} />;
    }
  }

  const urlParams = {
    ijt: trackingParam,
    paging_context: 'similar_postings',
  };

  const nwtTrackingOptions = {
    event_schema: 'searchresult',
    page: 'jobs/detail',
    tracking_token: trackingToken,
    position: position,
  };

  if (job) {
    const url = `/jobs/${job.slug}`;
    const trackingUrl = `${url}${toQuery(urlParams)}`;

    const onClick = (e: React.MouseEvent): void => {
      // we don't redirect the user if they use ctrl/option + click to open a new tab
      if (!e.metaKey && !e.ctrlKey) {
        redirectUrl(e, trackingUrl, history);
      }
    };

    const addTrackingParams = (e: React.MouseEvent<HTMLAnchorElement>): void => {
      e.preventDefault();
      e.currentTarget.href = trackingUrl;
    };

    const removeTrackingParams = (e: React.MouseEvent<HTMLAnchorElement>): void => {
      e.preventDefault();
      e.currentTarget.href = url;
    };

    return (
      <SimilarJobWithNWTTracking
        job={job}
        onClick={onClick}
        onMouseDown={addTrackingParams}
        onMouseLeave={removeTrackingParams}
        url={url}
        data-testid="similar-job"
        data-cy="similar-job"
        className={className}
        hideBookmark
        nwtTrackingOptions={nwtTrackingOptions}
        showRating={!!job.companyInfo.company?.kununuData}
      />
    );
  }

  return null;
};
